import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { AppNav } from './AppNav';
import { TicketManagement } from './TicketManagement';
import { WorkItemManagement } from './WorkItemManagement';
import { WorkItemDashboard } from './WorkItemDashboard';
import { ApiTester } from './ApiTester';
import { TicketEntryPage } from '../pages/TicketEntryPage';
import { AlertsPage } from '../pages/AlertsPage';
import { AdminPage } from '../pages/AdminPage';
import { CustomersPage } from '../pages/CustomersPage';
import { CalendarPage } from '../pages/CalendarPage';
import { WorkItemSearchPage } from '../pages/WorkItemSearchPage';
import { InvoicesPage } from '../pages/InvoicesPage';
import { LineItemExplorerPage } from '../pages/LineItemExplorerPage';
import { CallsPage } from '../pages/CallsPage';
import { PaymentsPage } from '../pages/PaymentsPage';
import { InventoryPage } from '../pages/InventoryPage';
import { PrintPage } from '../pages/PrintPage';
import { PartOrdersPage } from '../pages/PartOrdersPage';
import { PartsToOrderNotifier } from './part_orders/PartsToOrderNotifier';
import { TicketDetailPage } from '../pages/TicketDetailPage';
import { InvoiceDetailPage } from '../pages/InvoiceDetailPage';
import { CallDetailPage } from '../pages/CallDetailPage';
import { QuotesPage } from '../pages/QuotesPage';
import { CheckInsPage } from '../pages/CheckInsPage';
import { QuoteDetailPage } from '../pages/QuoteDetailPage';
import { CustomerDetailPage } from '../pages/CustomerDetailPage';
import { DomainDetailPage } from '../pages/DomainDetailPage';
import { CheckInCreatePage } from '../pages/CheckInCreatePage';
import { CheckInSessionDetailPage } from '../pages/CheckInSessionDetailPage';
import { CheckInByNumberPage } from '../pages/CheckInByNumberPage';
import { CheckInLinkedEntryPage } from '../pages/CheckInLinkedEntryPage';
import { CheckInDeviceDetailPage } from '../pages/CheckInDeviceDetailPage';
import { CheckInPrintSlipPage } from '../pages/CheckInPrintSlipPage';
import { TicketSheetPrintPage } from '../pages/TicketSheetPrintPage';
import { UserSettingsPage } from '../pages/UserSettingsPage';
import type { WorkItem } from '@techgitter/contracts';
import { FEATURE_TECH_CARD_V2 } from '../features';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { resolveAvatarColor } from '../styles/avatarPalette';

interface UserInfo {
  displayName: string;
  userPrincipalName: string;
  roles: string[];
}

interface MainLayoutProps {
  userInfo: UserInfo | null;
}

type ViewType = 'tickets' | 'workitems' | 'workitem-dashboard' | 'debug' | 'new-ticket' | 'edit-ticket' | 'alerts' | 'admin' | 'customers' | 'calendar' | 'new-appointment' | 'workitem-search' | 'invoices' | 'calls' | 'payments' | 'inventory' | 'print' | 'quotes' | 'part-orders' | 'new-check-in' | 'check-in-session' | 'check-in-device' | 'check-in-print' | 'check-ins' | 'ticket-sheet';

// AppNav has its own route keys; map them to the layout's ViewType values.
const NAV_ROUTE_TO_VIEW: Record<string, ViewType> = {
  'work-items': 'workitem-dashboard',
  search: 'workitem-search',
  invoices: 'invoices',
  calls: 'calls',
  'check-ins': 'check-ins',
  // The topbar's New check-in shortcut. Route key and view happen to share a
  // spelling, but the map has to hold every key a nav surface can emit.
  'new-check-in': 'new-check-in',
  payments: 'payments',
  quotes: 'quotes',
  'part-orders': 'part-orders',
  calendar: 'calendar',
  customers: 'customers',
  inventory: 'inventory',
  print: 'print',
  admin: 'admin',
};

const viewToNavRoute = (view: ViewType): string => {
  switch (view) {
    case 'workitem-dashboard':
      return 'work-items';
    case 'workitem-search':
      return 'search';
    case 'calendar':
    case 'new-appointment':
      return 'calendar';
    case 'new-check-in':
      return 'new-check-in';
    case 'invoices':
    case 'calls':
    case 'check-ins':
    case 'payments':
    case 'quotes':
    case 'part-orders':
    case 'customers':
    case 'inventory':
    case 'print':
    case 'admin':
      return view;
    default:
      return '';
  }
};

export const MainLayout: React.FC<MainLayoutProps> = ({ userInfo }) => {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const currentUser = useCurrentUser();
  const headerAvatarColor = currentUser
    ? resolveAvatarColor({ storedColor: currentUser.avatarColor, seed: currentUser.userId })
    : undefined;
  const [currentView, setCurrentView] = useState<ViewType>('workitem-dashboard');
  const [checkInSessionId, setCheckInSessionId] = useState<string | null>(null);
  const [checkInDeviceJobId, setCheckInDeviceJobId] = useState<string | null>(null);
  // Seeded by the "Create Return" button on a closed-job detail page.
  // CheckInCreatePage reads this once on mount to pre-fill customer + device
  // fields and lock intakeType to 'in_store_return'.
  const [pendingReturnOfWorkItem, setPendingReturnOfWorkItem] = useState<WorkItem | null>(null);
  const lastDashboardViewRef = useRef<ViewType>('workitem-dashboard');

  // Detect URL-based routes
  const ticketMatch = location.pathname.match(/^\/ticket\/(\d+)$/);
  const invoiceMatch = location.pathname.match(/^\/invoice\/(.+)$/);
  const callMatch = location.pathname.match(/^\/call\/(.+)$/);
  const quoteMatch = location.pathname.match(/^\/quote\/(.+)$/);
  const customerMatch = location.pathname.match(/^\/customer\/(.+)$/);
  const domainMatch = location.pathname.match(/^\/domain\/(.+)$/);
  const quotesListMatch = location.pathname === '/quotes';
  // The Line Item Explorer — a tab of the Invoices page with its own URL so a
  // search page can be bookmarked; the Invoices nav tab stays active on it.
  const lineItemsMatch = /^\/invoices\/line-items\/?$/.test(location.pathname);
  // Both spellings, because the friendly per-check-in link below is the one
  // people type and a bare `/checkins` shouldn't dead-end on the dashboard.
  const checkInsListMatch =
    location.pathname === '/check-ins' || location.pathname === '/checkins';
  // `/checkins/2026092702` — a check-in addressed by the number on its printed
  // slip, the way `/ticket/12345` addresses a ticket. Resolved to the canonical
  // session URL by CheckInByNumberPage.
  const checkInByNumberMatch = location.pathname.match(/^\/check-?ins\/([0-9]+)\/?$/);
  // `/check-in/new?dropoff=12345` / `?return=12345` — a Drop Off or Return
  // check-in pre-linked to job #12345. Where a scan of a released device's
  // label lands (scan target `start_dropoff`), and the Return button on a
  // finished job's scan summary. The plain new-check-in view has no URL.
  const newCheckInParams = location.pathname.replace(/\/$/, '') === '/check-in/new'
    ? new URLSearchParams(location.search)
    : null;
  const linkedCheckIn: { link: 'dropoff' | 'return'; number: string } | null =
    newCheckInParams?.get('dropoff')
      ? { link: 'dropoff', number: newCheckInParams.get('dropoff') as string }
      : newCheckInParams?.get('return')
        ? { link: 'return', number: newCheckInParams.get('return') as string }
        : null;
  // Matches /admin and /admin/:slug so every admin menu item is deep-linkable.
  const adminMatch = location.pathname.match(/^\/admin(?:\/([a-z0-9_-]+))?\/?$/);
  // The signed-in user's own settings, opened from their name in the topbar.
  const settingsMatch = /^\/settings\/?$/.test(location.pathname);
  // Check-In deep links. The RS ticket raised from a check-in carries the
  // per-device URL in its opening comment (see _build_rs_ticket_payload in
  // sm4rt-orchestrator), so these have to resolve for anyone following it —
  // the sessionStorage hand-off below only works from inside the app.
  const checkInSessionMatch = location.pathname.match(
    /^\/check-in\/session\/([0-9a-fA-F-]+)\/?$/,
  );
  const checkInDeviceMatch = location.pathname.match(
    /^\/check-in\/session\/([0-9a-fA-F-]+)\/job\/([0-9a-fA-F-]+)\/?$/,
  );

  useEffect(() => {
    if (
      currentView !== 'new-ticket' &&
      currentView !== 'edit-ticket' &&
      currentView !== 'new-check-in' &&
      currentView !== 'check-in-session' &&
      currentView !== 'check-in-device' &&
      currentView !== 'check-in-print' &&
      currentView !== 'ticket-sheet'
    ) {
      lastDashboardViewRef.current = currentView;
    }
  }, [currentView]);

  const handleBackFromNewTicket = () => {
    setCurrentView(lastDashboardViewRef.current);
  };

  // Arriving on /invoices/line-items (a bookmark, or the Invoices page's
  // switch) leaves the layout on the invoices view, so the switch's "Invoices"
  // — a plain navigate('/') — lands on the invoice list, not the dashboard.
  useEffect(() => {
    if (lineItemsMatch) setCurrentView('invoices');
  }, [lineItemsMatch]);

  const handleNavigate = (view: string) => {
    // If we're on a URL-based route, navigate back to root first
    if (ticketMatch || invoiceMatch || callMatch || quoteMatch || customerMatch || domainMatch || quotesListMatch || lineItemsMatch || adminMatch || settingsMatch || checkInSessionMatch || checkInDeviceMatch || checkInsListMatch || checkInByNumberMatch || linkedCheckIn) {
      if (view === 'quotes') {
        routerNavigate('/quotes');
      } else if (view === 'check-ins') {
        routerNavigate('/check-ins');
      } else if (view === 'admin') {
        routerNavigate('/admin');
      } else {
        routerNavigate('/');
      }
    } else if (view === 'quotes') {
      routerNavigate('/quotes');
    } else if (view === 'check-ins') {
      routerNavigate('/check-ins');
    } else if (view === 'admin') {
      routerNavigate('/admin');
    }
    // Leaving the new-check-in view clears any pending Return seed so a
    // subsequent fresh check-in doesn't see stale data.
    if (view !== 'new-check-in') {
      setPendingReturnOfWorkItem(null);
    }
    setCurrentView(view as ViewType);
  };

  const handleCreateReturn = (workItem: WorkItem) => {
    setPendingReturnOfWorkItem(workItem);
    // If we're on a URL-based route (like /ticket/:id), pop back to root so
    // the view-switch below renders the create page.
    if (ticketMatch || invoiceMatch || callMatch || quoteMatch || customerMatch || domainMatch || quotesListMatch) {
      routerNavigate('/');
    }
    setCurrentView('new-check-in');
  };

  const renderCurrentView = () => {
    // URL-based routes take precedence
    if (ticketMatch) {
      return <TicketDetailPage userInfo={userInfo} onCreateReturn={handleCreateReturn} />;
    }
    if (invoiceMatch) {
      return <InvoiceDetailPage userInfo={userInfo} />;
    }
    if (callMatch) {
      return <CallDetailPage />;
    }
    if (quoteMatch) {
      return <QuoteDetailPage />;
    }
    if (customerMatch) {
      return <CustomerDetailPage />;
    }
    if (domainMatch) {
      return <DomainDetailPage />;
    }
    if (quotesListMatch) {
      return <QuotesPage />;
    }
    if (lineItemsMatch) {
      return <LineItemExplorerPage />;
    }
    if (checkInsListMatch) {
      return <CheckInsPage />;
    }
    if (checkInByNumberMatch) {
      return <CheckInByNumberPage sessionNumber={checkInByNumberMatch[1]} />;
    }
    if (linkedCheckIn) {
      return (
        <CheckInLinkedEntryPage
          key={`${linkedCheckIn.link}:${linkedCheckIn.number}`}
          link={linkedCheckIn.link}
          ticketNumber={linkedCheckIn.number}
          onBack={() => routerNavigate('/')}
          onCreated={sessionId => routerNavigate(`/check-in/session/${sessionId}`, { replace: true })}
        />
      );
    }
    if (settingsMatch) {
      return <UserSettingsPage />;
    }
    if (adminMatch) {
      return (
        <AdminPage
          tabSlug={adminMatch[1] || undefined}
          onSelectTab={slug => routerNavigate(`/admin/${slug}`)}
        />
      );
    }
    if (checkInDeviceMatch) {
      return (
        <CheckInDeviceDetailPage
          sessionId={checkInDeviceMatch[1]}
          jobId={checkInDeviceMatch[2]}
          onBack={() => routerNavigate(`/check-in/session/${checkInDeviceMatch[1]}`)}
        />
      );
    }
    if (checkInSessionMatch) {
      return (
        <CheckInSessionDetailPage
          sessionId={checkInSessionMatch[1]}
          onBack={() => routerNavigate('/')}
          onOpenDevice={(sessionId, jobId) =>
            routerNavigate(`/check-in/session/${sessionId}/job/${jobId}`)
          }
          onPrintSlip={sessionId => {
            // The slip has no URL route of its own; drop back to the view
            // switch, which renders it from component state.
            setCheckInSessionId(sessionId);
            setCurrentView('check-in-print');
            routerNavigate('/');
          }}
        />
      );
    }

    switch (currentView) {
      case 'tickets':
        return <TicketManagement />;
      case 'workitems':
        return <WorkItemManagement />;
      case 'workitem-dashboard':
        return <WorkItemDashboard onNavigate={handleNavigate} userInfo={userInfo} />;
      case 'debug':
        // Dev only — the tester is hard-wired to localhost:7071.
        return process.env.NODE_ENV !== 'production' ? <ApiTester /> : null;
      // Distinct keys: both cases render TicketEntryPage at the same position,
      // so without them React reuses the instance and the Link button's
      // edit → new-ticket switch would carry the edited ticket's state over.
      case 'new-ticket':
        return <TicketEntryPage key="new-ticket" onBack={handleBackFromNewTicket} />;
      case 'edit-ticket': {
        const editJson = sessionStorage.getItem('edit_workitem_json');
        const editWorkItem = editJson ? JSON.parse(editJson) : undefined;
        return (
          <TicketEntryPage
            key="edit-ticket"
            onBack={handleBackFromNewTicket}
            editWorkItem={editWorkItem}
            onCreateLinked={() => handleNavigate('new-ticket')}
          />
        );
      }
      case 'alerts':
        return <AlertsPage />;
      case 'admin':
        return <AdminPage />;
      case 'customers':
        return <CustomersPage userInfo={userInfo} />;
      case 'calendar':
        return <CalendarPage />;
      case 'new-appointment':
        return <CalendarPage openNewAppointment />;
      case 'workitem-search':
        return <WorkItemSearchPage userInfo={userInfo} />;
      case 'invoices':
        return <InvoicesPage userInfo={userInfo} />;
      case 'calls':
        return <CallsPage userInfo={userInfo} />;
      case 'payments':
        return <PaymentsPage userInfo={userInfo} />;
      case 'inventory':
        return <InventoryPage userInfo={userInfo} />;
      case 'print':
        return <PrintPage />;
      case 'quotes':
        return <QuotesPage />;
      case 'check-ins':
        return <CheckInsPage />;
      case 'part-orders':
        return <PartOrdersPage />;
      case 'new-check-in':
        return (
          <CheckInCreatePage
            onBack={handleBackFromNewTicket}
            onCreated={sessionId => {
              setCheckInSessionId(sessionId);
              setCurrentView('check-in-session');
            }}
            returnOfWorkItem={pendingReturnOfWorkItem}
          />
        );
      case 'check-in-session':
        if (!checkInSessionId) {
          return <WorkItemDashboard onNavigate={handleNavigate} userInfo={userInfo} />;
        }
        return (
          <CheckInSessionDetailPage
            sessionId={checkInSessionId}
            onBack={() => {
              setCheckInSessionId(null);
              setCurrentView(lastDashboardViewRef.current);
            }}
            onOpenDevice={(sessionId, jobId) => {
              setCheckInSessionId(sessionId);
              setCheckInDeviceJobId(jobId);
              setCurrentView('check-in-device');
            }}
            onPrintSlip={sessionId => {
              setCheckInSessionId(sessionId);
              setCurrentView('check-in-print');
            }}
          />
        );
      case 'check-in-device':
        if (!checkInSessionId || !checkInDeviceJobId) {
          return <WorkItemDashboard onNavigate={handleNavigate} userInfo={userInfo} />;
        }
        return (
          <CheckInDeviceDetailPage
            sessionId={checkInSessionId}
            jobId={checkInDeviceJobId}
            onBack={() => {
              setCheckInDeviceJobId(null);
              setCurrentView('check-in-session');
            }}
          />
        );
      case 'check-in-print':
        if (!checkInSessionId) {
          return <WorkItemDashboard onNavigate={handleNavigate} userInfo={userInfo} />;
        }
        return (
          <CheckInPrintSlipPage
            sessionId={checkInSessionId}
            autoPrint
            onBack={() => setCurrentView('check-in-session')}
          />
        );
      case 'ticket-sheet': {
        // The work item is handed off via sessionStorage (same pattern as
        // 'edit-ticket') so we don't have to thread the object through state.
        const sheetJson = sessionStorage.getItem('ticket_sheet_workitem_json');
        const sheetWorkItem: WorkItem | null = sheetJson ? JSON.parse(sheetJson) : null;
        if (!sheetWorkItem) {
          return <WorkItemDashboard onNavigate={handleNavigate} userInfo={userInfo} />;
        }
        return (
          <TicketSheetPrintPage
            workItem={sheetWorkItem}
            onBack={() => setCurrentView(lastDashboardViewRef.current)}
          />
        );
      }
      default:
        return <WorkItemDashboard onNavigate={handleNavigate} userInfo={userInfo} />;
    }
  };

  // Grey page surface (scoped) for views that use the card-on-grey design system.
  const noUrlRoute =
    !ticketMatch && !invoiceMatch && !callMatch && !quoteMatch && !customerMatch && !domainMatch && !quotesListMatch && !lineItemsMatch && !adminMatch && !settingsMatch
    && !checkInSessionMatch && !checkInDeviceMatch && !checkInsListMatch && !checkInByNumberMatch
    && !linkedCheckIn;
  const surfaceModifier =
    ticketMatch
      ? ' main-content--ticket'
      : invoiceMatch
      ? ' main-content--invoice'
      : callMatch
      ? ' main-content--call'
      : quoteMatch
      ? ' main-content--quote'
      : customerMatch
      ? ' main-content--customer'
      : adminMatch
      ? ' main-content--admin'
      : settingsMatch
      ? ' main-content--settings'
      : checkInSessionMatch || checkInDeviceMatch
      ? ' main-content--checkin'
      : linkedCheckIn || (noUrlRoute && currentView === 'new-check-in')
      ? ' main-content--newcheckin'
      : noUrlRoute && FEATURE_TECH_CARD_V2 && currentView === 'workitem-dashboard'
      ? ' main-content--workitems'
      : noUrlRoute && currentView === 'workitem-search'
        ? ' main-content--search'
        : noUrlRoute && currentView === 'invoices'
          ? ' main-content--invoices'
          : noUrlRoute && currentView === 'calls'
            ? ' main-content--calls'
          : noUrlRoute && currentView === 'payments'
            ? ' main-content--payments'
            : lineItemsMatch
      ? ' main-content--invoices'
      : quotesListMatch || (noUrlRoute && currentView === 'quotes')
              ? ' main-content--quotes'
              : checkInsListMatch || (noUrlRoute && currentView === 'check-ins')
              ? ' main-content--checkins'
              : noUrlRoute && currentView === 'customers'
                ? ' main-content--customers'
                : noUrlRoute && currentView === 'inventory'
                  ? ' main-content--inventory'
                  : noUrlRoute && currentView === 'print'
                    ? ' main-content--print'
                    : noUrlRoute && currentView === 'part-orders'
                    ? ' main-content--part-orders'
                    : noUrlRoute && (currentView === 'calendar' || currentView === 'new-appointment')
                      ? ' main-content--calendar'
                      : noUrlRoute && (currentView === 'new-ticket' || currentView === 'edit-ticket')
                        ? ' main-content--newticket'
                        : noUrlRoute && currentView === 'admin'
                          ? ' main-content--admin'
                          : noUrlRoute && currentView === 'check-in-session'
                            ? ' main-content--checkin'
                            : '';

  // One active route for both navigation surfaces: the tab strip and the
  // topbar's utility cluster, which holds Calls / Calendar / Print / Admin.
  const activeNavRoute = adminMatch
    ? 'admin'
    : settingsMatch
      ? ''
      : quotesListMatch
      ? 'quotes'
      : lineItemsMatch
      ? 'invoices'
      : checkInsListMatch
        ? 'check-ins'
        : viewToNavRoute(currentView);

  const handleNavRoute = (route: string) =>
    handleNavigate(NAV_ROUTE_TO_VIEW[route] ?? route);

  return (
    <div className="main-layout">
      <Header
        userName={userInfo?.displayName ?? ''}
        avatarColor={headerAvatarColor}
        activeRoute={activeNavRoute}
        onNavChange={handleNavRoute}
        onBellClick={() => handleNavigate('alerts')}
        onAccountClick={() => routerNavigate('/settings')}
        accountActive={settingsMatch}
      />

      <AppNav activeRoute={activeNavRoute} onNavChange={handleNavRoute} />

      <PartsToOrderNotifier onView={() => handleNavigate('part-orders')} />

      <main className={`main-content${surfaceModifier}`}>
        {renderCurrentView()}
      </main>
    </div>
  );
};